/**
 * Session / authentication. The session itself is the HttpOnly cookie `creator_session`; the
 * client keeps the user info and the per-session CSRF token from /auth/login and /auth/me
 * (rotated sessions – password change, user switch – return a new token).
 */
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { api, RateLimitedError, UnauthorizedError } from '../api/client';
import type { Me } from '../api/types';

export interface LoginInput {
  username: string;
  password: string;
  totp?: string;
  actAs?: string;
}

export type LoginResult =
  | { ok: true; me: Me }
  | { ok: false; reason: 'totp_required' | 'totp_invalid' | 'invalid' | 'throttled' | 'unavailable' | 'error'; retryAfter?: number; message?: string };

/** GET /auth/policy: public, non-sensitive login UI flags and the instance branding. */
export interface PublicPolicy {
  allowSwitchUser: boolean;
  totp: { available: boolean };
  password: { minLength: number; maxBytes: number };
  defaultLanguage: string | null;
  branding: {
    productName: string;
    description: string | null;
    theme: string;
    logoUrl: string;
    faviconUrl: string;
    iconUrl: string;
    splashUrl: string;
    color: string | null;
  };
}

export const DEFAULT_POLICY: PublicPolicy = {
  allowSwitchUser: false,
  totp: { available: false },
  password: { minLength: 8, maxBytes: 1024 },
  defaultLanguage: null,
  branding: {
    productName: 'Creator',
    description: null,
    theme: 'default',
    logoUrl: '/favicon.svg',
    faviconUrl: '/favicon.svg',
    iconUrl: '/icons/icon-192.png',
    splashUrl: '',
    color: null,
  },
};

/** Point the document favicon at the instance branding. */
export function applyBranding(p: PublicPolicy, doc: Document | undefined = typeof document !== 'undefined' ? document : undefined): void {
  if (!doc) return;
  const icon = doc.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (icon && p.branding.faviconUrl && icon.getAttribute('href') !== p.branding.faviconUrl) {
    icon.setAttribute('href', p.branding.faviconUrl);
    icon.removeAttribute('type');
  }
}

export const useSessionStore = defineStore('session', () => {
  const me = ref<Me | null>(null);
  /** unknown until the first /auth/me answered */
  const status = ref<'unknown' | 'authenticated' | 'anonymous'>('unknown');
  let probing: Promise<boolean> | undefined;

  const authenticated = computed(() => status.value === 'authenticated');
  const csrfToken = computed(() => me.value?.csrfToken);
  const displayName = computed(() => me.value?.user.displayName || me.value?.user.username || '');
  const impersonating = computed(() => !!me.value?.impersonatedBy);
  /** public policy (defaults until /auth/policy answered) */
  const policy = ref<PublicPolicy>(DEFAULT_POLICY);
  const policyLoaded = ref(false);
  let policyLoading: Promise<PublicPolicy> | undefined;

  function setMe(m: Me | null): void {
    me.value = m;
    status.value = m ? 'authenticated' : 'anonymous';
  }

  /** GET /auth/policy once (the browser revalidates it, max-age 300); failures keep the defaults. */
  function loadPolicy(force = false): Promise<PublicPolicy> {
    if (policyLoaded.value && !force) return Promise.resolve(policy.value);
    policyLoading ??= (async () => {
      try {
        const p = await api.get<PublicPolicy>('/auth/policy', { allowUnauthorized: true });
        policy.value = { ...DEFAULT_POLICY, ...p, branding: { ...DEFAULT_POLICY.branding, ...p.branding } };
        policyLoaded.value = true;
        applyBranding(policy.value);
      } catch (e) {
        console.warn('auth/policy not available', e);
      }
      return policy.value;
    })().finally(() => (policyLoading = undefined));
    return policyLoading;
  }

  /** GET /auth/me (once; later calls reuse the answer). Resolves to "authenticated". */
  function ensure(): Promise<boolean> {
    if (status.value !== 'unknown') return Promise.resolve(authenticated.value);
    probing ??= refresh().finally(() => (probing = undefined));
    return probing;
  }

  async function refresh(): Promise<boolean> {
    try {
      setMe(await api.get<Me>('/auth/me', { allowUnauthorized: true }));
    } catch (e) {
      if (e instanceof UnauthorizedError) setMe(null);
      else {
        // server unreachable: stay "unknown" so the next navigation retries
        status.value = 'unknown';
        throw e;
      }
    }
    return authenticated.value;
  }

  async function login(input: LoginInput): Promise<LoginResult> {
    const body: LoginInput = { username: input.username.trim(), password: input.password };
    if (input.totp) body.totp = input.totp.replace(/\s+/g, '');
    if (input.actAs?.trim()) body.actAs = input.actAs.trim();
    try {
      const m = await api.post<Me>('/auth/login', body, { allowUnauthorized: true });
      setMe(m);
      return { ok: true, me: m };
    } catch (e) {
      if (e instanceof RateLimitedError) return { ok: false, reason: 'throttled', ...(e.retryAfter !== undefined ? { retryAfter: e.retryAfter } : {}) };
      if (e instanceof UnauthorizedError) {
        if (e.code === 'totp_required') return { ok: false, reason: 'totp_required' };
        if (e.code === 'invalid_totp' || e.code === 'totp_invalid') return { ok: false, reason: 'totp_invalid' };
        return { ok: false, reason: body.totp ? 'totp_invalid' : 'invalid', message: e.message };
      }
      const status = (e as { status?: number }).status;
      if (status === 503) return { ok: false, reason: 'unavailable' };
      return { ok: false, reason: 'error', message: (e as Error).message };
    }
  }

  /** POST /auth/logout and forget the session locally. */
  async function logout(): Promise<void> {
    try {
      if (authenticated.value) await api.post('/auth/logout', undefined, { allowUnauthorized: true });
    } catch {
      // the local state is cleared anyway
    }
    clear();
  }

  /** Forget the session locally (expired, 401). */
  function clear(): void {
    setMe(null);
  }

  async function changePassword(oldPassword: string, newPassword: string): Promise<void> {
    setMe(await api.post<Me>('/auth/password', { oldPassword, newPassword }));
  }

  async function switchUser(username: string): Promise<void> {
    setMe(await api.post<Me>('/auth/switch-user', { username }));
  }

  async function switchBack(): Promise<void> {
    setMe(await api.post<Me>('/auth/switch-back'));
  }

  async function totpEnroll(): Promise<{ secret: string; otpauthUrl: string }> {
    return api.post('/auth/totp/enroll');
  }

  async function totpVerify(code: string): Promise<string[]> {
    const r = await api.post<{ backupCodes: string[] }>('/auth/totp/verify', { code: code.replace(/\s+/g, '') });
    await refresh();
    return r.backupCodes;
  }

  async function totpDisable(password: string, code: string): Promise<void> {
    await api.post('/auth/totp/disable', { password, code: code.replace(/\s+/g, '') });
    await refresh();
  }

  return {
    me,
    status,
    authenticated,
    csrfToken,
    displayName,
    impersonating,
    policy,
    policyLoaded,
    loadPolicy,
    ensure,
    refresh,
    login,
    logout,
    clear,
    changePassword,
    switchUser,
    switchBack,
    totpEnroll,
    totpVerify,
    totpDisable,
  };
});
